import React from 'react';
import {
  MATFILE_ENDLESS_TYPE,
  AUTOFILL_PATTERN,
} from 'pxc-editor-core/constants';
import { rotateRect90deg } from 'pxc-editor-core/helpers/calculation';
import { isMultiColored as isMaterialMultiColored } from 'pxc-editor-core/helpers/material';
import CmmePageLengthControl from 'pxc-editor-web/src/components/CmmePageLengthControl/CmmePageLengthControl';
import { colors as _colors } from '@/globalStyles';
import {
  getFirstSelectedSection,
  getShield,
  isWebComponentEnvironment,
  createClassName,
  isActiveElementEditable
} from '@/helpers';
import svgElementClickableListener from '../../../hoc/svgElementClickableListener';
import PrintingOffsetsRect from '../../shared/PrintingOffsetsRect';
import DebuggingGridRect from '../../shared/DebuggingGridRect';
import PageLengthControl from './PageLengthControl';
import PageSectionRenderer from './PageSectionRenderer';
import PageShieldsRenderer from './PageShieldsRenderer';
import PageShapeRenderer from './PageShapeRenderer';
import MaterialInfoBox from './MaterialInfoBox';
import MaterialOutline from './MaterialOutline';
import SyncButton from './SyncButton';
import MatrixBorders from './MatrixBorders';
import MatrixSelection from './MatrixSelection';
import EditControls from './EditControls';
import { SYSTEM_STROKE_WIDTH, systemStrokeWidthProps } from './styles';
import { COPY_EXCEL_MODE } from '@/constants';
import { connect } from 'react-redux';
import { fillSectionsFromExcelThunk } from '@/store/actions/fill-sections-from-excel';
import { showSnack } from '../../../../pxc_editor_web/src/actions/snackbar';

//Variables
const CLIPBOARD_TEXT_TYPE = 'text/plain';
const CLIPBOARD_HTML_TYPE = 'text/html';
const EXCEL_MIME_TYPE = 'application/vnd.ms-excel';
const UNSUPPORTED_CONTENT_MESSAGE = 'Unsupported clipboard content.';
const NO_SELECTED_CELL_MESSAGE = 'There is no selected cell to paste into.';
const PASTE_PROCESSING_ERROR = 'Paste processing failed:';

// Excel-specific HTML signatures
const EXCEL_OFFICE_SCHEMA = 'xmlns:o="urn:schemas-microsoft-com:office:office"';
const EXCEL_MSO_PREFIX = 'mso-';
const EXCEL_CLASS_PREFIX = 'class=xl';
const TABLE_TAG = '<table';
const GOOGLE_SHEETS_URL = 'docs.google.com/spreadsheets';
const GOOGLE_SHEETS_ID = 'id="sheets-';
const GOOGLE_SHEETS_GENERATOR = 'meta name="generator" content="Google Sheets"';

// Default data for fillSections settings
const defaultData = {
  mode: COPY_EXCEL_MODE,
  pattern: AUTOFILL_PATTERN.NEXT_CELL,
  isMatrix: false,
  labelsList: [],
};

const SvgGroupWrap = props => {
  const { svgElementClickableRef, ...otherProps } = props;
  return <g {...otherProps} ref={svgElementClickableRef} />;
};

const ClickableSvgGroup = svgElementClickableListener(SvgGroupWrap);

class PageRenderer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      dataFromExcelText: [],
      dataFromExcelStyles: [],
    };
  }

  static getDerivedStateFromProps() {
    return null;
  }

  componentDidMount() {
    document.addEventListener('paste', this.handlePaste);
  }

  componentWillUnmount() {
    document.removeEventListener('paste', this.handlePaste);
  }

  onPageGroupFastClickTap = () => {
    this.props.handlePageClick(this.props.page.uid);
  };

  // Handle paste event from clipboard
  handlePaste = e => {
    if (isActiveElementEditable()) {
      return;
    }

    e.preventDefault();

    // Retrieve clipboard data
    const clipboardData = e.clipboardData || window.clipboardData;
    const text = clipboardData.getData(CLIPBOARD_TEXT_TYPE);
    const html = clipboardData.getData(CLIPBOARD_HTML_TYPE);
    const typesList = clipboardData.types || [];
    const types = Array.from(typesList);

    // Check if clipboard content is from Excel
    const isExcel =
      types.includes(EXCEL_MIME_TYPE) ||
      (html &&
        (html.includes(EXCEL_OFFICE_SCHEMA) ||
          html.includes(EXCEL_MSO_PREFIX) ||
          html.includes(EXCEL_CLASS_PREFIX) ||
          html.includes(TABLE_TAG)));

    // Check if clipboard content is from Google Sheets
    const isGoogleSheets =
      html &&
      (html.includes(GOOGLE_SHEETS_URL) ||
        html.includes(GOOGLE_SHEETS_ID) ||
        html.includes(GOOGLE_SHEETS_GENERATOR));

    // Validate clipboard content
    if (
      (!text ||
        types.some(
          type => type && type.startsWith && type.startsWith('image/')
        )) &&
      !isExcel &&
      !isGoogleSheets
    ) {
      console.error(UNSUPPORTED_CONTENT_MESSAGE);
      this.props.snackbar(UNSUPPORTED_CONTENT_MESSAGE);
      return;
    }

    try {
      // Parse clipboard text into rows and cells
      const rows = (text || '')
        .split('\n')
        .map(row => row.replace(/\r/g, ''))
        .filter(row => row.trim());
      let data = rows.map(row => row.split('\t').filter(cell => cell.trim()));

      if (data[0]?.length > 1) {
        data = data.filter(item => item !== undefined).flat();
      } else {
        data = data.flat();
      }

      const {
        sectionsSelection,
        pages,
        fillSectionsFromExcelThunk,
        isFromWizard,
      } = this.props;
      const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);

      if (!firstSelSection) {
        console.warn(NO_SELECTED_CELL_MESSAGE);
        return;
      }

      // Prepare settings for fillSections
      const settings = {
        ...defaultData,
        labelsList: data.map(text => ({
          text,
        })),
      };

      // Dispatch fillSections with prepared settings
      if (!isFromWizard) {
        fillSectionsFromExcelThunk(settings);
      }
    } catch (err) {
      console.error(PASTE_PROCESSING_ERROR, err);
    }
  };

  render() {
    const {
      isSelected,
      pages,
      page,
      horPageOffset,
      scaleCoef,
      materialType,
      materialPrintParameters,
      isContentPlacingDebugging,
      isDebuggingGrid,
      shieldsSelection,
      sectionsSelection,
      isGridDisplayed,
      gridWidth,
      colors,
      buildPageLengthLabelHelper,
      moveSection,
      resizeSection,
      onShapeClick,
      onSectionDblClick,
      fictiveSections,
      selWizard,
      setIsCableFlagSyncState,
      userSizeUnit,
    } = this.props;
    const {
      resizeType,
      box: { l, t, r, b },
      holes,
      solid,
      decorations,
      layouts,
      transforms,
      uid,
      sections,
    } = page;

    if (Object.values(sectionsSelection).length) {
      Object.values(sections).forEach(section => {
        fictiveSections[section.id] = JSON.parse(JSON.stringify(section));
      });
    } else {
      Object.values(fictiveSections).forEach(section => {
        delete fictiveSections[section.id];
      });
      Object.values(sections).forEach(section => {
        fictiveSections[section.id] = JSON.parse(JSON.stringify(section));
      });
    }

    // convert rect of a solid collection into paths, group by stroke & fill colors
    const newSolid = [];
    const generatedGroups = {};
    for (let i = 0; i < solid.length; i++) {
      if (solid[i].primitiveType === 'rect') {
        const groupId = Math.random()
          .toString(36)
          .substring(7);
        const fillColor = solid[i].fillColor;
        const strokeColor = solid[i].strokeColor;
        const background_style = solid[i].primitiveProps?.background_style;
        const outline_style = solid[i].primitiveProps?.outline_style;
        generatedGroups[groupId] = generatedGroups[groupId]
          ? generatedGroups[groupId]
          : { d: '', background_style, outline_style, fillColor, strokeColor };
        const x = Math.round(solid[i].primitiveProps.x * 1000) / 1000;
        const y = Math.round(solid[i].primitiveProps.y * 1000) / 1000;
        const xX = Math.round((x + solid[i].primitiveProps.w) * 1000) / 1000;
        const yY = Math.round((y + solid[i].primitiveProps.h) * 1000) / 1000;
        if (solid[i].primitiveProps.roundedCornerRadius > 0) {
          const r = solid[i].primitiveProps.roundedCornerRadius;
          generatedGroups[groupId].d += `
            M${x + r},${y}L${xX - r},${y}
            A${r} ${r} 1 0 1 ${xX} ${y + r}
            L${xX},${yY - r}
            A${r} ${r} 1 0 1 ${xX - r} ${yY}
            L${x + r},${yY}
            A${r} ${r} 1 0 1 ${x} ${yY - r}
            L${x},${y + r}
            A${r} ${r} 1 0 1 ${x + r} ${y}
          `;
        } else {
          generatedGroups[groupId].d += `
            M${x},${y}
            L${xX},${y}
            L${xX},${yY}
            L${x},${yY}
            L${x},${y}
          `;
        }
      } else {
        newSolid.push(solid[i]);
      }
    }
    if (!!Object.keys(generatedGroups).length) {
      Object.entries(generatedGroups).forEach(([key, value]) => {
        newSolid.push({
          id: uid + '-solid-path-' + key + '-' + new Date().getTime(),
          primitiveType: 'path',
          primitiveProps: { ...value },
          strokeColor: value.strokeColor,
          fillColor: value.fillColor,
        });
      });
    }

    // convert lines of decorations to path
    const newDecorations = [];
    let generatedD = '';
    let generatedDPrintable = '';
    for (let i = 0; i < decorations.length; i++) {
      if (
        decorations[i].primitiveType === 'line' &&
        !decorations[i].printable
      ) {
        generatedD += `
          M${decorations[i].primitiveProps.start_point.x},${decorations[i].primitiveProps.start_point.y}
          L${decorations[i].primitiveProps.end_point.x},${decorations[i].primitiveProps.end_point.y}
        `;
      } else if (
        decorations[i].primitiveType === 'line' &&
        decorations[i].printable
      ) {
        generatedDPrintable += `
          M${decorations[i].primitiveProps.start_point.x},${decorations[i].primitiveProps.start_point.y}
          L${decorations[i].primitiveProps.end_point.x},${decorations[i].primitiveProps.end_point.y}
        `;
      } else {
        newDecorations.push(decorations[i]);
      }
    }
    if (generatedD.length > 0) {
      newDecorations.push({
        id: uid + '-decoration-path-' + new Date().getTime(),
        primitiveType: 'path',
        primitiveProps: {
          d: generatedD,
        },
      });
    }
    if (generatedDPrintable.length > 0) {
      newDecorations.push({
        id: uid + '-printable-decoration-path-' + new Date().getTime(),
        primitiveType: 'path',
        primitiveProps: {
          d: generatedDPrintable,
        },
      });
    }

    const shieldsToRender = [];
    let selectedShield = null;
    const firstSelSection = getFirstSelectedSection(sectionsSelection, pages);
    selectedShield = firstSelSection
      ? getShield(layouts, firstSelSection.shieldId)
      : null;

    const isMatrixShield = {}; //fastest way: check all shields for isMatrix one time only and add it to isMatrixShield
    for (const layoutUid in layouts) {
      const { groups } = layouts[layoutUid];
      for (const groupUid in groups) {
        const { shields } = groups[groupUid];
        for (const shieldId in shields) {
          isMatrixShield[shieldId] = shields[shieldId].isMatrix;
          if (shieldId === page?.nearestPageShieldId && !selectedShield) {
            selectedShield = shields[shieldId];
          }
          shieldsToRender.push(shields[shieldId]);
        }
      }
    }

    const pageRectStrokeStyle = { stroke: _colors.black, strokeWidth: 0.1 };
    if (isSelected || isContentPlacingDebugging) {
      pageRectStrokeStyle.stroke = colors.pageSelectionFrame;
      pageRectStrokeStyle.strokeWidth = SYSTEM_STROKE_WIDTH;
    } else {
      pageRectStrokeStyle.stroke = 'none';
      pageRectStrokeStyle.strokeWidth = 0;
    }
    // calc transforms str
    let transformStr = `scale(${scaleCoef})`;
    let corrBox = { x: l, y: t, width: r, height: b };
    let corrMirX = transforms.mirror_x;
    let corrMirY = transforms.mirror_y;
    if (transforms.angle === 90 || transforms.angle === 270) {
      corrBox = rotateRect90deg(l, t, r, b);
      const oldMirX = corrMirX;
      corrMirX = corrMirY;
      corrMirY = oldMirX;
    }
    if (corrMirX) {
      transformStr = `${transformStr} scale(-1, 1) translate(-${l * 2 + r}, 0)`;
    }
    if (corrMirY) {
      transformStr = `${transformStr} scale(1, -1) translate(0, -${t * 2 + b})`;
    }
    if (transforms.angle) {
      transformStr = `${transformStr} rotate(${transforms.angle} ${l +
        r / 2} ${t + b / 2})`;
    }

    // calculate selection radius only one time
    const selRadius = gridWidth > 1 ? 0.15 : 0.15 * gridWidth;
    const isMaterialMultiColoredState = isMaterialMultiColored(
      materialPrintParameters
    );
    const sectionsTop =
      selectedShield?.primitiveProps?.y ||
      shieldsToRender[0]?.primitiveProps.y ||
      0;
    const sectionsBottom =
      sectionsTop + selectedShield?.primitiveProps?.h ||
      sectionsTop + shieldsToRender[0]?.primitiveProps.h ||
      0;

    let flagLength = selWizard?.data?.flagLength;
    flagLength =
      selWizard?.data?.pages && selWizard?.data?.pages[page.uid]
        ? Math.ceil(selWizard?.data?.pages[page.uid]?.flagLength * 10) / 10
        : Math.ceil(selWizard?.data?.flagLength * 10) / 10;
    const flagL100 = selWizard?.data ? Math.ceil(flagLength * 100) : 0;

    let cableFlagWizardPoligon = selWizard
      ? '' +
        'M' +
        l +
        ' ' +
        (sectionsTop - (sectionsTop - t) / 4) +
        'L' +
        l +
        ' ' +
        t +
        'L' +
        r +
        ' ' +
        t +
        'L' +
        r +
        ' ' +
        (sectionsTop - (sectionsTop - t) / 4)
      : '';
    let cableFlagWizardDashPoligon = selWizard
      ? '' +
        'M' +
        (l + flagL100 / 100) +
        ' ' +
        (sectionsTop - (sectionsTop - t) / 4) +
        'L' +
        (l + flagL100 / 100) +
        ' ' +
        t +
        'M' +
        (r - flagL100 / 100) +
        ' ' +
        (sectionsTop - (sectionsTop - t) / 4) +
        'L' +
        (r - flagL100 / 100) +
        ' ' +
        t
      : '';
    const cfwsp = 26 / scaleCoef;
    let cableFlagWizardSyncPoligon = selWizard
      ? '' +
        'M' +
        (l + flagL100 / 200) +
        ' ' +
        (b + cfwsp * 0.5) +
        'A' +
        cfwsp * 0.5 +
        ' ' +
        cfwsp * 0.5 +
        ' 0 0 0 ' +
        (l + flagL100 / 200 + cfwsp * 0.5) +
        ' ' +
        (b + cfwsp * 1) +
        'L' +
        (r - flagL100 / 200 - cfwsp * 0.5) +
        ' ' +
        (b + cfwsp * 1) +
        'A' +
        cfwsp * 0.5 +
        ' ' +
        cfwsp * 0.5 +
        ' 0 0 0 ' +
        (r - flagL100 / 200) +
        ' ' +
        (b + cfwsp * 0.5)
      : '';
    const cableFlagWizardButton = selWizard
      ? {
          x: l + (r - l) / 2 - cfwsp,
          y: b,
          r: cfwsp,
        }
      : {
          x: 0,
          y: 0,
          r: 0,
        };
    const isEndlessMaterial = materialType === MATFILE_ENDLESS_TYPE;
    const clickable = false;
    const className = createClassName({ clickable });

    return (
      <>
        <MaterialInfoBox transform={transformStr} />
        <ClickableSvgGroup
          id={uid}
          className={className}
          transform={`translate(${l + horPageOffset * scaleCoef}, 0)`}
          clickable={clickable}
          onFastClickTap={this.onPageGroupFastClickTap}
          onDblClickTap={null}
        >
          <g id={`render-group-${uid}`} transform={transformStr}>
            <MaterialOutline
              x={l}
              y={t}
              width={r}
              height={b}
              pageRectStrokeStyle={pageRectStrokeStyle}
            />
            {newSolid.map(shapeObj => (
              <PageShapeRenderer
                key={shapeObj.id}
                shapeObj={shapeObj}
                useFineStrocke={!!selWizard}
              />
            ))}
            {holes.map(shapeObj => (
              <PageShapeRenderer
                key={shapeObj.id}
                shapeObj={shapeObj}
                useFineStrocke={false}
              />
            ))}
            <PageShieldsRenderer
              shieldsToRender={shieldsToRender}
              shieldsSelection={shieldsSelection}
              isContentPlacingDebugging={isContentPlacingDebugging}
              page={page}
              onShapeClick={onShapeClick}
              isGridDisplayed={isGridDisplayed}
              gridWidth={gridWidth}
              selRadius={selRadius}
              selWizard={selWizard}
            />
            {selectedShield && selWizard?.application_type === 'CABLE_FLAG' && (
              <>
                <path
                  d={cableFlagWizardPoligon}
                  fill="none"
                  stroke="black"
                  strokeWidth={0.1}
                />
                <path
                  d={cableFlagWizardDashPoligon}
                  fill="none"
                  stroke="black"
                  strokeDasharray="0.75,0.35"
                  strokeWidth={0.1}
                />
                <SyncButton
                  x={cableFlagWizardButton.x}
                  y={cableFlagWizardButton.y}
                  width={cableFlagWizardButton.r * 2}
                  height={cableFlagWizardButton.r * 2}
                  selWizard={selWizard}
                  cableFlagWizardSyncPoligon={cableFlagWizardSyncPoligon}
                  setIsCableFlagSyncState={setIsCableFlagSyncState}
                />
              </>
            )}
            {isContentPlacingDebugging && (
              <PrintingOffsetsRect
                pageBox={page.box}
                printingOffsets={materialPrintParameters?.printing_offsets}
                style={systemStrokeWidthProps}
              />
            )}
            {isDebuggingGrid && (
              <DebuggingGridRect
                pageBox={page.box}
                printingOffsets={materialPrintParameters?.printing_offsets}
                style={systemStrokeWidthProps}
              />
            )}
            {newDecorations.map(shapeObj => (
              <PageShapeRenderer
                key={shapeObj.id}
                className="decoration"
                shapeObj={shapeObj}
                useFineStrocke={!!selWizard}
              />
            ))}
            {Object.values(sections).map(section => {
              const isSectionSelected = !!sectionsSelection[section.id];

              return (
                <PageSectionRenderer
                  key={section.id}
                  page={page}
                  section={section}
                  isMultiColored={isMaterialMultiColoredState}
                  isSectionSelected={isSectionSelected}
                  moveSection={moveSection}
                  resizeSection={resizeSection}
                  onSectionDblClick={onSectionDblClick}
                />
              );
            })}
            <MatrixBorders
              sections={sections}
              selectedShield={selectedShield}
              isMatrixShield={isMatrixShield}
              shieldsToRender={shieldsToRender}
            />
            <MatrixSelection
              page={page}
              selectedShield={selectedShield}
              fictiveSections={fictiveSections}
              isMatrixShield={isMatrixShield}
            />
            <EditControls />
          </g>
          {isEndlessMaterial && !selWizard && (
            <CmmePageLengthControl
              resizeType={resizeType}
              pageX={corrBox.x * scaleCoef}
              pageY={corrBox.y * scaleCoef}
              buildPageLengthLabelHelper={buildPageLengthLabelHelper}
              labelOnly={
                selectedShield?.isMatrix &&
                selectedShield?.isMatrixFixedCellWidth
              }
              selectedPage={page}
              userSizeUnit={userSizeUnit}
            />
          )}
          {isEndlessMaterial && selWizard?.application_type === 'CABLE_FLAG' && (
            <>
              <PageLengthControl
                resizeType={resizeType}
                pageX={corrBox.x * scaleCoef - 15}
                pageY={corrBox.y * scaleCoef}
                pageWidth={Math.ceil(flagLength * 10) / 10}
                buildPageLengthLabelHelper={buildPageLengthLabelHelper}
                labelOnly={true}
              />
              <PageLengthControl
                resizeType={resizeType}
                pageX={
                  (corrBox.x + Math.ceil(flagLength * 10) / 10) * scaleCoef - 15
                }
                pageY={corrBox.y * scaleCoef}
                pageWidth={Math.ceil(selWizard.data.flagMidLength)}
                buildPageLengthLabelHelper={buildPageLengthLabelHelper}
                labelOnly={true}
              />
              <PageLengthControl
                resizeType={resizeType}
                pageX={
                  (corrBox.x +
                    Math.ceil(
                      (flagLength + selWizard.data.flagMidLength) * 10
                    ) /
                      10) *
                    scaleCoef -
                  15
                }
                pageY={corrBox.y * scaleCoef}
                pageWidth={Math.ceil(flagLength * 10) / 10}
                buildPageLengthLabelHelper={buildPageLengthLabelHelper}
                labelOnly={true}
              />
            </>
          )}
        </ClickableSvgGroup>
      </>
    );
  }
}

// Map state to props
const mapStateToProps = state => ({
  sectionsSelection: state.core.sectionsSelection,
  pages: state.core.pages,
  isFromWizard: state.core.isFromWizard,
  preselectedPrinter: state.core.preselectedPrinter,
  printerList: state.core.printerList,
});

// Map dispatch to props
const mapDispatchToProps = dispatch => ({
  fillSectionsFromExcelThunk: data => dispatch(fillSectionsFromExcelThunk(data)),
  snackbar: text =>
    dispatch(
      showSnack({
        intlId: 'app.editor.snackbar.copiedExcelError',
        message: text,
        defaultMessage: text,
        variant: 'error',
        show: true,
      })
    ),
});

// Connect component to Redux store
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(PageRenderer));
